import dayjs from "dayjs";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { DirectDebitRunPayment } from "../../../../model/payments/DirectDebitRunPayment";
import { PointOfSaleTransaction } from "../../../../model/pointofsale/PointOfSaleTransaction";
import { MemberCancellation } from "../../../../model/members/MemberCancellation";
import { HealthFormSummary } from "../../../../model/members/HealthFormSummary";
import { QuestionnaireSchema } from "../../forms/QuestionnaireSchema";
import { computeFlaggedAnswers } from "../../forms/healthFlags";
import {
    hasFailedMembershipPayments,
    hasIncompletePosPayments,
    hasOutstandingMembershipPayments,
    isFailedMembershipPayment,
    isOutstandingMembershipPayment,
} from "../incompletePayments";
import { BannerRule, evaluateBannerRules } from "../bannerRules";
import { MemberProfileMode } from "./modeConfig";
import { daysLeft, isExpired, statusMeta, EXPIRY_URGENT_DAYS } from "./validity";

export type AlertSeverity = "error" | "warning";

// Where an alert's action button should take the user (a workspace tab key).
export type AlertActionTarget = "tx" | "health";

export interface ProfileAlert {
    severity: AlertSeverity;
    message: string;
    actionLabel?: string;
    actionTarget?: AlertActionTarget;
}

export interface AlertInputs {
    member: MemberProfile;
    payments: DirectDebitRunPayment[];
    pointOfSaleTransactions: PointOfSaleTransaction[];
    cancellations: MemberCancellation[];
    healthSummary: HealthFormSummary | null;
    healthSchema: QuestionnaireSchema;
    // Client-configurable banner rules (banners.rulesJson), parsed once by the
    // screen. A fired membershipExpiry rule drives the expiry alert.
    bannerRules: BannerRule[];
}

// Bank-return statuses on a direct-debit run payment. The rejection import
// persists every bank return as Rejected; Cancelled / NoAccount are matched
// defensively for older rows. Every one means the money was not collected.
const DD_RETURN_STATUSES = ["Rejected", "Cancelled", "NoAccount"];

// Run payments whose outcome isn't known yet. A newly created run's Pending row
// must not hide an earlier rejection that was never collected.
const DD_UNSETTLED_STATUSES = ["Pending", "Unknown"];

// Rules-driven alerts for the "Needs attention" summary, mirroring the existing
// StatusBanner / bannerRules logic — just re-presented as a list. Errors (red):
// terminal states (banned/cancelled/inactive/expired), a rejected or failed last
// direct debit, and a failed, unpaid or still-pending membership payment at the
// till (FORT-454 / FORT-455). Warnings (amber): scheduled cancellation, other incomplete POS
// payments, expiry (a configured banner rule, else the built-in <=14d warning)
// and unreviewed flagged health answers. Non-urgent expiry with no configured
// rule stays in the validity meter only (not duplicated here).
export function computeAlerts(mode: MemberProfileMode, inputs: AlertInputs): ProfileAlert[] {
    const { member, payments, pointOfSaleTransactions, cancellations, healthSummary, healthSchema, bannerRules } = inputs;
    const alerts: ProfileAlert[] = [];

    // Terminal membership states.
    if (member.status === "Banned") {
        alerts.push({ severity: "error", message: "Member is banned from the club" });
    } else if (member.status === "Cancelled") {
        alerts.push({ severity: "error", message: "Membership has been cancelled" });
    } else if (!member.isEnabled) {
        alerts.push({ severity: "error", message: "Membership is not active" });
    } else if (isExpired(member)) {
        alerts.push({ severity: "error", message: "Membership has expired" });
    }

    // Scheduled (active, upcoming) cancellation.
    const today = dayjs();
    const scheduledCancellation = (cancellations ?? []).find(
        (c) => c.cancellationActive && dayjs(c.cancellationDate).isAfter(today, "day"),
    );
    if (scheduledCancellation) {
        alerts.push({
            severity: "warning",
            message: `Scheduled cancellation on ${dayjs(scheduledCancellation.cancellationDate).format("ddd DD MMM YYYY")}`,
        });
    }

    // Point-of-sale money. A failed, unpaid or still-pending membership payment
    // means the member has not paid for their membership (red); any other
    // incomplete payment is a warning, so the same transaction never raises both.
    const posAction = mode.pos ? { actionLabel: "View", actionTarget: "tx" as const } : {};
    if (hasFailedMembershipPayments(pointOfSaleTransactions)) {
        alerts.push({ severity: "error", message: "Membership payment failed", ...posAction });
    }
    if (hasOutstandingMembershipPayments(pointOfSaleTransactions)) {
        alerts.push({ severity: "error", message: "Membership payment outstanding", ...posAction });
    }
    const otherPosTransactions = (pointOfSaleTransactions ?? []).filter(
        (t) => !isFailedMembershipPayment(t) && !isOutstandingMembershipPayment(t),
    );
    if (hasIncompletePosPayments(otherPosTransactions)) {
        alerts.push({ severity: "warning", message: "Member has incomplete payments", ...posAction });
    }

    // Last settled direct-debit payment returned by the bank or failed (red). A
    // later successful collection supersedes it — this reads the latest payment
    // only, not an outstanding balance, so the wording says what it detects.
    const lastPayment = (payments ?? []).filter((p) => !DD_UNSETTLED_STATUSES.includes(p.paymentStatus)).sort((a, b) =>
        dayjs(b.paymentDate).diff(dayjs(a.paymentDate)),
    )[0];
    if (lastPayment) {
        const date = dayjs(lastPayment.paymentDate).format("ddd DD MMM YYYY");
        if (DD_RETURN_STATUSES.includes(lastPayment.paymentStatus)) {
            alerts.push({ severity: "error", message: `Direct debit payment for ${date} was rejected` });
        } else if (lastPayment.paymentStatus === "Failed") {
            alerts.push({ severity: "error", message: `Direct debit payment for ${date} failed` });
        }
    }

    // Membership expiry. A configured banner rule (banners.rulesJson) takes
    // precedence and drives the message/severity; otherwise fall back to the
    // built-in urgent-expiry warning (within 14 days). Only applies while the
    // membership is still active and not already expired (terminal states above).
    const left = daysLeft(member);
    if (member.isEnabled && !isExpired(member)) {
        const configBanner = evaluateBannerRules(bannerRules ?? [], member);
        if (configBanner && (configBanner.severity === "warning" || configBanner.severity === "error")) {
            alerts.push({ severity: configBanner.severity, message: configBanner.message });
        } else if (left <= EXPIRY_URGENT_DAYS) {
            alerts.push({ severity: "warning", message: `Membership expires in ${left} days` });
        }
    }

    // Staff cue: a not-active member whose core details are present is ready for
    // staff to activate. Only shown to STAFF (admin/front-desk — mode.actions is
    // off in self-service) so members don't see it, and only when the profile
    // looks complete (name + at least one contact field) so it doesn't add noise
    // for barely-started records. This is an ADDITIONAL cue alongside the
    // existing "Membership is not active" error, not a replacement.
    const readyStatuses = ["Inactive", "Pending", "Cancelled"];
    const hasContact = !!(member.phoneNumber || member.emailAddress || member.addressLine1);
    if (
        mode.actions &&
        !member.isEnabled &&
        readyStatuses.includes(member.status) &&
        !!member.name &&
        hasContact
    ) {
        alerts.push({ severity: "warning", message: "Member is ready to activate" });
    }

    // Unreviewed flagged health answers.
    if (mode.health && healthSummary && healthSummary.submissionId !== null) {
        const flagged = computeFlaggedAnswers(healthSchema, healthSummary.answers);
        const reviewed = healthSummary.reviews.length > 0;
        if (flagged.length > 0 && !reviewed) {
            alerts.push({
                severity: "warning",
                message: `${flagged.length} flagged health answer${flagged.length === 1 ? "" : "s"} awaiting review`,
                actionLabel: "Review",
                actionTarget: "health",
            });
        }
    }

    return alerts;
}

// Whole-screen status signal (generalises the old getBackgroundColor red/orange/
// none tint). Critical = a terminal membership state or any error alert; amber
// attention = any outstanding warning; clear otherwise. Drives the canvas tint
// + identity-header accent bar via a data-status attribute on the screen root.
export type ScreenStatus = "clear" | "attention" | "critical";

export function screenStatus(member: MemberProfile, alerts: ProfileAlert[]): ScreenStatus {
    if (statusMeta(member).tone === "err" || alerts.some((a) => a.severity === "error")) {
        return "critical";
    }
    if (alerts.some((a) => a.severity === "warning")) {
        return "attention";
    }
    return "clear";
}
